import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { validators, formats, dates } from 'investira.sdk';
import { Button, IconButton, Tooltip, Stack, Icon, Chip } from 'investira.react.components';
import { BulkContextActions, DataTable } from '../';
import usePerfil from '../../../hooks/usePerfil';

/**
 * Renderiza o componente AvaliacoesDataGrid.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Array} props.data - Propriedade `data` do componente.
 * @param {Object} props.state - Propriedade `state` do componente.
 * @param {Object} props.initialState - Propriedade `initialState` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const AvaliacoesDataGrid = props => {
  const {
    state,
    output_id,
    onEditClick,
    onViewClick,
    onBulkDeleteClick,
    onDeleteClick,
    onSwitchChange,
    renderDetailPanel,
    data,
    ...restProps
  } = props;

  const { usuarioPrioridade } = usePerfil();

  const columns = useMemo(
    () => [
      {
        accessorKey: 'gestor',
        header: 'Gestor',
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (validators.isNull(value)) {
            return;
          }
          if (typeof value === 'object') {
            return value.pessoa_fantasia || value.pessoa || '';
          }
          return value;
        }
      },
      {
        accessorKey: 'fundo',
        header: 'Fundo',
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (validators.isNull(value)) {
            return;
          }
          if (typeof value === 'object') {
            return value.ativo_seuid || value.ativo || value.nome || '';
          }
          return value;
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }

          return (
            <Chip
              size="small"
              label={cell.getValue()}
              color={
                cell.getValue() === 'Aguardando respostas'
                  ? 'error'
                  : cell.getValue() === 'Rascunho'
                  ? 'warning'
                  : cell.getValue() === 'Concluído pelo Gestor'
                  ? 'info'
                  : cell.getValue() === 'Pendente'
                  ? 'info'
                  : 'success'
              }
            />
          );
        }
      },
      {
        accessorKey: 'score',
        header: 'Score',
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return (
            <Chip
              size="small"
              label={cell.getValue()}
              color={
                cell.getValue() < 1.8
                  ? 'error'
                  : cell.getValue() < 2.2
                  ? 'warning'
                  : cell.getValue() < 3
                  ? 'info'
                  : 'primary'
              }
            />
          );
        }
      },
      {
        accessorKey: 'modified',
        header: 'Última Modificação',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return cell.getValue();
        }
      },
      {
        accessorKey: 'reenvio',
        header: 'Reenvio',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (!cell.getValue()) {
            return;
          }
          return (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                // TODO: implementar a ação que o botão deve realizar
              }}>
              Reenviar email
            </Button>
          );
        }
      }
    ],
    []
  );

  const xProps = {
    columns,
    data,
    state,
    enableRowSelection: usuarioPrioridade <= 6,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: ''
      }
    },
    renderTopToolbarCustomActions: ({ table }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }

      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;

      return (
        <BulkContextActions
          onDeleteClick={() => {
            onBulkDeleteClick(selectedRows);
          }}
          disabled={state.isLoading || state.showProgressBars || !isSomeRowSelected}
        />
      );
    },
    renderRowActions: ({ row }) => {
      console.log('row', row.original);
      return (
        <Stack direction="row" spacing={2}>
          <Tooltip title="Editar Rascunho">
            <IconButton
              color="primary"
              onClick={() => onEditClick(row.original)}
              disabled={
                !['Concluído pelo Gestor', 'Pendente', 'Rascunho'].includes(row.original.status)
              }>
              <Icon iconName="edit" size={21} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Visualizar Avaliação">
            <IconButton
              color="primary"
              onClick={() => onViewClick(row.original)}
              disabled={['Pendente', 'Rascunho', 'Aguardando respostas'].includes(
                row.original.status
              )}>
              <Icon iconName="eye" size={21} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    },
    ...restProps
  };

  return <DataTable {...xProps} />;
};

AvaliacoesDataGrid.propTypes = {
  data: PropTypes.array.isRequired,
  state: PropTypes.object,
  initialState: PropTypes.object
};

export default AvaliacoesDataGrid;
